import styled from '@emotion/styled';
import { Fab, Grid } from '@mui/material';
import { PageSection } from '@skoda/k2tools-app-components';
import * as queryString from 'query-string';
import * as React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { StaticContext } from 'react-router';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { VinAppContext } from '../../contexts/VinContext';
import { ErrorCode } from '../../enums/ErrorCode';
import { SearchError } from '../../enums/searchError';
import { IUrlParameters } from '../../models/urlParameters';
import { DropdownSelect } from '../filter/dropdownSelect';
import { ISelectOptionProps } from '../filter/filterSelectBase';
import { VinInput } from '../filter/vinInput';
import styles from './styles.scss';

const ReCaptchaContainer = styled.div`
	& > div > div > .grecaptcha-badge {
		z-index: -1000;
	}
`;

type RouteState = {
	isScrolledToVin?: boolean;
};

type VinSearchProps = RouteComponentProps<{}, StaticContext, RouteState> & {
	onlyLanguage: boolean;
	onSubmit: (vin: string, language: string) => void;
	setVinReset?: (func: () => void) => void;
	onError?: (error: SearchError, vin: string) => void;
	defaultVinCode?: string;
	hasVinError?: boolean;
};

type VinSearchState = {
	selectedLanguage: string;
	vin: string;
	languages: ISelectOptionProps[];
	isScrolled: boolean;
	formErrorText: string;
	isSubmitted: boolean;
	disabled: boolean;
	recaptchaToken?: string;
};

class VinSearch extends React.Component<VinSearchProps, VinSearchState> {
	static contextType = VinAppContext;
	context: React.ContextType<typeof VinAppContext>;

	vinInputRef: React.RefObject<VinInput>;
	vinSearchRef: React.RefObject<HTMLDivElement>;
	recaptchaRef: React.RefObject<ReCAPTCHA>;

	constructor(props: VinSearchProps) {
		super(props);
		this.state = {
			selectedLanguage: null,
			vin: '',
			languages: [],
			isScrolled: this.props.location.state?.isScrolledToVin ?? null,
			formErrorText: null,
			isSubmitted: false,
			disabled: true,
		};
		this.vinInputRef = React.createRef();
		this.vinSearchRef = React.createRef();
		this.recaptchaRef = React.createRef();
	}

	componentDidMount() {
		if (this.props.defaultVinCode) {
			this.setState({ vin: this.props.defaultVinCode });
		}
		if (this.state.isScrolled) {
			window.scrollTo(0, this.vinSearchRef.current.offsetTop);
		}
		this.props.setVinReset?.(this.handleVinReset);
		if (this.props.defaultVinCode && this.props.hasVinError) {
			this.vinInputRef.current?.handleVinValidationProgress(false, false);
		}
		if (!this.context.vin) {
			const params = this.getUrlParams();
			if (params.vin) {
				this.context.initData(params, this.loadDataFromContext);
			}
			if (params.vin && !params.manualLang) {
				this.props.onError?.(SearchError.ParametersMismatch, params.vin);
			}
		} else {
			this.loadDataFromContext();
		}
	}

	componentWillUnmount() {
		this.context.clearUrlParameters();
	}

	handleVinCodeSubmit = (val: string, isValidFormat: boolean) => {
		if (isValidFormat) {
			this.setState({ vin: val, selectedLanguage: null, languages: [], recaptchaToken: undefined }, () => {
				this.vinInputRef.current?.handleVinValidationProgress(true, false);
				if (this.recaptchaRef.current) {
					console.log('recaptcha with key ' + window.OwnersManuals.baseAppSettings.reCaptchaKey + ' is now triggered');
					this.recaptchaRef.current.reset();
					this.recaptchaRef.current.execute(); // This triggers the invisible reCAPTCHA
				} else {
					this.handleVinLanguagesLoad();
				}
			});
		} else {
			this.setState({
				vin: null,
				selectedLanguage: null,
				languages: [],
				recaptchaToken: undefined,
			});
		}
	};

	handleVinCodeSubmitError = () => {
		this.context.setError('Search_Error_Title', 'Search_Error_Description');
	};

	handleLanguageSubmit = (val: ISelectOptionProps, isValid: boolean) => {
		this.setState((prev) => ({
			...prev,
			languages: prev.languages.map<ISelectOptionProps>((lang) => ({ ...lang, isSelected: lang.key === val.key })),
			selectedLanguage: val ? val.key : null,
			formErrorText: null,
			isSubmitted: false,
			disabled: false,
		}));
	};

	handleSubmit = () => {
		if (this.state.isSubmitted || !this.state.vin || !this.state.selectedLanguage) {
			return;
		}
		this.setState({ isSubmitted: true }, () => {
			this.context.submitVinSearch(this.state.vin, this.state.selectedLanguage);
			this.props.onSubmit(this.state.vin, this.state.selectedLanguage);
		});
	};

	handleVinReset = () => {
		if (this.state.vin) {
			this.setState({ vin: null }, () => {
				this.context.setVin(null);
			});
			this.vinInputRef.current?.handleVinClear();
		}
	};

	loadDataFromContext = () => {
		this.setState({ vin: this.context.vin, selectedLanguage: this.context.vinLanguage }, this.handleVinLanguagesLoad);
	};

	getUrlParams = () => {
		const parseParams = queryString.parse(this.props.location.search);
		return parseParams as object as IUrlParameters;
	};

	handleLanguagesClear = () => {
		this.setState({ languages: [], selectedLanguage: null });
	};

	handleVinLanguagesLoadError = (message: string, vin: string) => {
		this.handleLanguagesClear();
		this.vinInputRef.current?.handleVinValidationProgress(false, false);
		this.context.setError(`${message}_Title`, `${message}_Description`);
		this.props.onError?.(SearchError.UnrecoginzedVin, vin);
	};

	onReCAPTCHAChange = (token: string | null) => {
		console.log('recaptcha change invoked');
		if (token) {
			console.log('reCAPTCHA token:', token);
			this.setState({ recaptchaToken: token });
			this.handleVinLanguagesLoad();
		} else {
			if (this.recaptchaRef.current) {
				console.log('recaptcha has expired and is now triggered again');
				this.recaptchaRef.current.reset();
				this.recaptchaRef.current.execute();
			}
		}
	};

	handleVinLanguagesLoad = async () => {
		if (!this.state.vin) {
			return;
		}

		try {
			const vinResult = await this.context.getVinLanguages(this.state.vin, this.state.recaptchaToken);
			if (vinResult.errorCode === ErrorCode.NoError) {
				var langs = vinResult.results.map<ISelectOptionProps>((x) => ({
					isSelected: this.state.selectedLanguage && (x.code === this.state.selectedLanguage || x.omsCode === this.state.selectedLanguage),
					key: x.code,
					value: x.title,
				}));
				this.setState({ languages: langs });
				if (
					this.context.urlParameters &&
					this.context.urlParameters.vin &&
					this.context.urlParameters.manualLang &&
					langs.find((x) => x.isSelected) == null
				) {
					this.setState({ selectedLanguage: null, formErrorText: this.context.services.resourceProvider.getText('VIN_Form_Choose_Valid_Language') });
					this.props.onError?.(SearchError.LanguageNotFound, this.context.urlParameters.vin);
				}

				if (this.state.selectedLanguage && !this.props.onlyLanguage) {
					this.setState({ selectedLanguage: langs.find((x) => x.isSelected).key });
				}

				this.vinInputRef.current?.handleVinValidationProgress(false, true);
			} else if (vinResult.errorCode === ErrorCode.UnrecoginzedVin) {
				this.handleVinLanguagesLoadError('Vin_Search_NotFound', this.state.vin);
			} else {
				throw Error(vinResult.errorCode);
			}
		} catch (error) {
			this.handleVinLanguagesLoadError('Search_Error', this.state.vin);
		}
	};

	getForm = () => {
		return (
			<form noValidate autoComplete='off' className={styles['container-form']}>
				{window.OwnersManuals.baseAppSettings.reCaptchaKey && (
					<ReCaptchaContainer>
						<ReCAPTCHA
							ref={this.recaptchaRef}
							sitekey={window.OwnersManuals.baseAppSettings.reCaptchaKey}
							size='invisible'
							onChange={this.onReCAPTCHAChange}
						/>
					</ReCaptchaContainer>
				)}
				{!this.props.onlyLanguage ? (
					<Grid container spacing={5}>
						<Grid item lg={5} md={7} sm={7} xs={12}>
							<VinInput
								ref={this.vinInputRef}
								onVinSubmit={this.handleVinCodeSubmit}
								onVinSubmitError={this.handleVinCodeSubmitError}
								defaultValue={this.state.vin}
							/>
						</Grid>
						<Grid item lg={4} md={5} sm={5} xs={12}>
							<DropdownSelect
								isDisabled={!this.state.languages?.length || !this.state.vin}
								onSubmit={this.handleLanguageSubmit}
								defaultValue={this.context.services.resourceProvider.getText('Language', 'Language')}
								options={this.state.vin && this.state.languages.length > 0 ? this.state.languages : []}
								error={this.state.formErrorText}
							/>
						</Grid>
						<Grid item lg={3} md={12} sm={12} xs={12}>
							<Fab
								disabled={!this.state.selectedLanguage || !this.state.vin}
								color='primary'
								onClick={(event) => {
									event.preventDefault();
									this.handleSubmit();
								}}
								variant='extended'
								className={styles['btn-submit']}
							>
								{this.context.services.resourceProvider.getText('Show_manual')}
							</Fab>
						</Grid>
					</Grid>
				) : (
					<Grid container spacing={5} className={styles['language-only-grid']}>
						<Grid item lg={8} md={8} sm={12} xs={12}>
							<DropdownSelect
								isDisabled={!this.state.languages?.length || !this.state.vin}
								onSubmit={this.handleLanguageSubmit}
								defaultValue={this.context.services.resourceProvider.getText('Language', 'Language')}
								options={this.state.vin && this.state.languages.length > 0 ? this.state.languages : []}
								error={this.state.formErrorText}
							/>
						</Grid>
						<Grid item lg={4} md={4} sm={12} xs={12}>
							<Fab
								disabled={!this.state.selectedLanguage || !this.state.vin}
								color='primary'
								onClick={(event) => {
									event.preventDefault();
									this.handleSubmit();
								}}
								variant='extended'
								className={styles['btn-submit']}
							>
								{this.context.services.resourceProvider.getText('Show_manual')}
							</Fab>
						</Grid>
					</Grid>
				)}
			</form>
		);
	};

	render() {
		return (
			<React.Fragment>
				{!this.props.onlyLanguage && (
					<PageSection>
						<div className={`${styles['container-vin-search']}`} ref={this.vinSearchRef}>
							<h2 className={`${styles['heading']}`}>{this.context.services.resourceProvider.getText('VIN_search')}</h2>
							{this.getForm()}
						</div>
					</PageSection>
				)}
				{this.props.onlyLanguage && this.state.vin && this.getForm()}
			</React.Fragment>
		);
	}
}

export default withRouter(VinSearch);
